import { useQuery } from '@apollo/client';
import { useAtomValue, useAtom, useSetAtom } from 'jotai';
import { useCallback, useMemo, useEffect } from 'react';
import { PlainTableWidget, PlainTableRow } from './PlainTableWidget';
import { GET_EQALLOCATION_DATA } from '~/graphql/queries/eqallocation';
import {
  latestSnapshotIdAtom,
  triggerHardRefetchAtom,
  portfolioIdAtom,
} from '~/atoms/dashboardAtom';
import {
  endpointAllocationBatchUploadPOST,
  endpointAllocationBatchDeleteDELETE,
} from '~/api/def/endpoints/allocationBatchEndpoint';
import { endpointPortfolioAnalysisPOST } from '~/api/def/endpoints/portfolioAnalysisEndpoint';
import { toastAtom } from '~/atoms/toastAtom';
import type { Toast } from 'primereact/toast';

interface AllocationData {
  id: string;
  isin: string;
  percent: number;
  EQInstrument: {
    full_ticker: string;
    name: string;
    isin: string;
    sector: string;
  };
}

interface AllocationBatch {
  id: string;
  type: string;
  asOfDatetime: string;
  allocations: AllocationData[];
}

interface EQAllocationDataResponse {
  currentHoldings: Array<{ allocation: AllocationBatch }>;
  universe: Array<{ allocation: AllocationBatch }>;
  benchmark: Array<{ allocation: AllocationBatch }>;
  proposedHoldings: Array<{ allocation: AllocationBatch }>;
}

export type PlainTableWidgetWrapperProps = {
  handleRemoveWidget?: (widgetId: string) => void;
  widgetId: string;
  title?: string;
  /** Primary batch type to display in the first column (default: 'current_holdings') */
  primaryBatchType?: 'current_holdings' | 'universe' | 'benchmark' | 'proposed';
  /** Comparison batch type to display in the second column (default: 'proposed') */
  comparisonBatchType?:
    | 'current_holdings'
    | 'universe'
    | 'benchmark'
    | 'proposed';
  /** Custom label for primary column (overrides default based on batch type) */
  primaryColumnLabel?: string;
  /** Custom label for comparison column (overrides default based on batch type) */
  comparisonColumnLabel?: string;
  /** Callback to update the primary batch type in dashboard config */
  onBatchTypeChange?: (
    batchType: 'current_holdings' | 'universe' | 'benchmark' | 'proposed',
  ) => void;
};

export const PlainTableWidgetWrapper: React.FC<
  PlainTableWidgetWrapperProps
> = ({
  handleRemoveWidget,
  widgetId,
  title = 'Allocation Table',
  primaryBatchType = 'current_holdings',
  comparisonBatchType = 'proposed',
  primaryColumnLabel,
  comparisonColumnLabel,
  onBatchTypeChange,
}) => {
  const snapshotId = useAtomValue(latestSnapshotIdAtom);
  const portfolioId = useAtomValue(portfolioIdAtom);
  const [triggerHardRefetch] = useAtom(triggerHardRefetchAtom);
  const [toastCurrent] = useAtom<Toast | null>(toastAtom);
  const setLatestSnapshotId = useSetAtom(latestSnapshotIdAtom);

  const { data, loading, error, refetch } = useQuery<EQAllocationDataResponse>(
    GET_EQALLOCATION_DATA,
    {
      variables: { snapshotId },
      skip: !snapshotId,
      fetchPolicy: 'network-only',
    },
  );

  // Refetch when triggerHardRefetch changes
  useEffect(() => {
    if (triggerHardRefetch > 0 && snapshotId) {
      refetch();
    }
  }, [triggerHardRefetch, snapshotId, refetch]);

  const showToast = useCallback(
    (
      severity: 'success' | 'error' | 'warn',
      summary: string,
      detail: string,
      life: number = 3000,
    ) => {
      if (toastCurrent) {
        toastCurrent.show({ severity, summary, detail, life });
      }
    },
    [toastCurrent],
  );

  const handleCSVUpload = useCallback(
    async (
      csvData: Array<{
        isin: string;
        current_allocation: number;
        sector: string;
        ticker?: string;
        name?: string;
      }>,
      description: string,
      batchType: 'universe' | 'benchmark' | 'current_holdings' | 'proposed',
    ) => {
      try {
        // Convert CSV format to API format
        const allocations = csvData.map((item) => ({
          isin: item.isin,
          current_allocation: item.current_allocation,
          allocation_percent: item.current_allocation,
        }));

        if (batchType === 'current_holdings') {
          // Use snapshot creation endpoint for current holdings
          if (!portfolioId) {
            showToast(
              'error',
              'Upload Failed',
              'No portfolio ID available. Please select a portfolio first.',
            );
            return;
          }

          const snapshotResponse = await endpointPortfolioAnalysisPOST({
            portfolio_id: portfolioId,
            allocations: allocations,
            description: description || 'Manual upload',
            snapshot_type: 'manual',
          });

          if (snapshotResponse && snapshotResponse.snapshot_id) {
            // Update snapshot ID atom with the new snapshot
            setLatestSnapshotId(snapshotResponse.snapshot_id);

            showToast(
              'success',
              'Snapshot Created',
              `Successfully created snapshot with ${snapshotResponse.created_allocations} allocations.`,
            );
          }
        } else {
          // Use allocation batch upload endpoint for universe, benchmark, and proposed
          // If no snapshot exists yet, create an empty one first
          let currentSnapshotId = snapshotId;
          if (!currentSnapshotId) {
            if (!portfolioId) {
              showToast(
                'error',
                'Upload Failed',
                'No portfolio ID available. Please select a portfolio first.',
              );
              return;
            }
            const snapshotResponse = await endpointPortfolioAnalysisPOST({
              portfolio_id: portfolioId,
              allocations: [],
              description: `Initial snapshot for ${batchType}`,
              snapshot_type: 'manual',
            });
            if (snapshotResponse?.snapshot_id) {
              currentSnapshotId = snapshotResponse.snapshot_id;
              // Don't setLatestSnapshotId yet — setting it now triggers a
              // useQuery re-run against the empty snapshot before the batch
              // upload has completed. We set it after the batch upload below.
            } else {
              showToast(
                'error',
                'Upload Failed',
                'Failed to create snapshot. Please try again.',
              );
              return;
            }
          }

          const batchResponse = await endpointAllocationBatchUploadPOST(
            currentSnapshotId,
            {
              allocations: allocations.map((item) => ({
                isin: item.isin,
                allocation_percent: item.allocation_percent,
              })),
              description: description || undefined,
              batch_type: batchType,
            },
          );

          // Update snapshot ID from the batch response — this is the
          // definitive ID after the data has been committed.
          const finalSnapshotId =
            batchResponse.snapshot_id || currentSnapshotId;
          setLatestSnapshotId(finalSnapshotId);

          if (batchResponse.excluded_instruments.length > 0) {
            showToast(
              'warn',
              'Upload Completed with Warnings',
              `${batchResponse.created_allocations} ${batchType} allocations uploaded successfully. ${batchResponse.excluded_count} ISINs were excluded (instruments not found).`,
              5000,
            );
          } else {
            showToast(
              'success',
              'Upload Successful',
              `Successfully uploaded ${batchResponse.created_allocations} ${batchType} allocations.`,
            );
          }
        }

        // Brief delay to allow backend data to propagate before refetching
        await new Promise((resolve) => setTimeout(resolve, 500));
        await refetch();
      } catch (error) {
        console.error('CSV upload error:', error);
        const errorMessage =
          error instanceof Error ? error.message : 'Unknown error occurred';
        showToast(
          'error',
          'Upload Failed',
          `Failed to upload allocations: ${errorMessage}`,
          5000,
        );
        throw error;
      }
    },
    [snapshotId, portfolioId, refetch, showToast, setLatestSnapshotId],
  );

  const batchTypeToastLabel = (batchType: string) => {
    switch (batchType) {
      case 'current_holdings':
        return 'current holdings';
      case 'universe':
        return 'reference universe';
      case 'benchmark':
        return 'benchmark';
      case 'proposed':
        return 'proposed holdings';
      default:
        return batchType;
    }
  };

  const handleDeleteBatch = useCallback(
    async (batchId: string, batchType: string) => {
      if (!snapshotId) return;
      try {
        await endpointAllocationBatchDeleteDELETE(snapshotId, batchId);
        const isCurrentHoldings = batchType === 'current_holdings';
        if (isCurrentHoldings) {
          showToast(
            'success',
            'Deleted',
            `Your ${batchTypeToastLabel(
              batchType,
            )} and analysis results have been deleted. Upload a new file and run Analysis when ready.`,
          );
        } else {
          showToast(
            'success',
            'Deleted',
            `Your ${batchTypeToastLabel(
              batchType,
            )} has been deleted. Upload a new file and run Analysis when ready.`,
          );
        }
        await new Promise((resolve) => setTimeout(resolve, 300));
        await refetch();
      } catch (err) {
        showToast('error', 'Delete Failed', 'Request failed', 5000);
        throw err;
      }
    },
    [snapshotId, refetch, showToast],
  );

  // Helper function to get batch from response based on batch type
  const getBatchFromResponse = useCallback(
    (batchType: string) => {
      if (!data) return null;

      switch (batchType) {
        case 'current_holdings':
          return data.currentHoldings?.[0]?.allocation;
        case 'universe':
          return data.universe?.[0]?.allocation;
        case 'benchmark':
          return data.benchmark?.[0]?.allocation;
        case 'proposed':
          return data.proposedHoldings?.[0]?.allocation;
        default:
          return null;
      }
    },
    [data],
  );

  // Transform GraphQL data into table rows
  const tableData = useMemo<PlainTableRow[]>(() => {
    if (!data) return [];

    // Extract batches based on props
    const primaryBatch = getBatchFromResponse(primaryBatchType);
    const comparisonBatch = getBatchFromResponse(comparisonBatchType);

    if (!primaryBatch && !comparisonBatch) return [];

    // For Reference Universe widget: only show data when the API returned universe data.
    // Do not show rows built from proposed/AI Portfolio alone.
    if (primaryBatchType === 'universe' && !primaryBatch) return [];

    // Create a map of ISIN -> allocation data
    const allocationMap = new Map<
      string,
      {
        full_ticker: string;
        isin: string;
        current_holdings: number | null;
        proposed_holdings: number | null;
        company_name?: string;
      }
    >();

    // Process primary batch
    primaryBatch?.allocations.forEach((allocation) => {
      allocationMap.set(allocation.isin, {
        full_ticker: allocation.EQInstrument.full_ticker,
        isin: allocation.isin,
        current_holdings: allocation.percent,
        proposed_holdings: null,
        company_name: allocation.EQInstrument.name,
      });
    });

    // Process comparison batch
    comparisonBatch?.allocations.forEach((allocation) => {
      const existing = allocationMap.get(allocation.isin);
      if (existing) {
        existing.proposed_holdings = allocation.percent;
      } else {
        allocationMap.set(allocation.isin, {
          full_ticker: allocation.EQInstrument.full_ticker,
          isin: allocation.isin,
          current_holdings: null,
          proposed_holdings: allocation.percent,
          company_name: allocation.EQInstrument.name,
        });
      }
    });

    // Convert map to array and sort by ticker
    return Array.from(allocationMap.values()).sort((a, b) =>
      a.full_ticker.localeCompare(b.full_ticker),
    );
  }, [data, primaryBatchType, comparisonBatchType, getBatchFromResponse]);

  // Generate default column labels based on batch types if not provided
  const defaultPrimaryLabel = useMemo(() => {
    switch (primaryBatchType) {
      case 'current_holdings':
        return 'Current Holdings';
      case 'universe':
        return 'Reference Universe';
      case 'benchmark':
        return 'Benchmark';
      case 'proposed':
        return 'Proposed';
      default:
        return 'Primary';
    }
  }, [primaryBatchType]);

  const defaultComparisonLabel = useMemo(() => {
    switch (comparisonBatchType) {
      case 'current_holdings':
        return 'Current Holdings';
      case 'universe':
        return 'Reference Universe';
      case 'benchmark':
        return 'Benchmark';
      case 'proposed':
        return 'AI Portfolio';
      default:
        return 'Comparison';
    }
  }, [comparisonBatchType]);

  // Generate dynamic title based on primary batch type
  const dynamicTitle = useMemo(() => {
    switch (primaryBatchType) {
      case 'current_holdings':
        return 'Current Holdings';
      case 'universe':
        return 'Reference Universe';
      case 'benchmark':
        return 'Benchmark';
      default:
        return title || 'Allocation Table';
    }
  }, [primaryBatchType, title]);

  const primaryBatch = getBatchFromResponse(primaryBatchType);
  const primaryBatchId = primaryBatch?.id;
  const hasPrimaryData = !!primaryBatchId;

  const handleDelete = useCallback(async () => {
    if (primaryBatchId) {
      await handleDeleteBatch(primaryBatchId, primaryBatchType);
    }
  }, [primaryBatchId, primaryBatchType, handleDeleteBatch]);

  return (
    <PlainTableWidget
      data={tableData}
      isLoading={loading}
      error={error || null}
      refetch={refetch}
      handleRemoveWidget={handleRemoveWidget}
      widgetId={widgetId}
      title={dynamicTitle}
      snapshotId={snapshotId || undefined}
      onCSVUpload={handleCSVUpload}
      primaryColumnLabel={primaryColumnLabel || defaultPrimaryLabel}
      comparisonColumnLabel={comparisonColumnLabel || defaultComparisonLabel}
      primaryBatchType={primaryBatchType}
      comparisonBatchType={comparisonBatchType}
      onBatchTypeChange={onBatchTypeChange}
      onDelete={hasPrimaryData ? handleDelete : undefined}
    />
  );
};
